// O guardado do expediente, sem React e sem `api` dentro.
//
// Fica separado do hook por um motivo prático: as corridas que este arquivo
// resolve (resposta em voo voltando depois de um logout, ou depois de alguém
// salvar o horário) não se provam olhando a tela — com um buscador de mentira
// e promessas na mão, elas viram três linhas de teste
// (`cacheDeExpediente.check.mjs`).
//
// Regras, em uma frase cada:
// - o guardado é POR EMPRESA (sair e entrar com outra conta não pode mostrar
//   o expediente da anterior);
// - toda invalidação incrementa a GERAÇÃO da empresa, e resposta de geração
//   vencida não volta para o guardado;
// - LIMPAR (logout) não pede recarga a ninguém; INVALIDAR (salvou horário)
//   pede — a diferença é o que impedia o cache de renascer depois do logout.
export const criarCacheDeExpediente = (buscarDoServidor, validadeMs) => {
  const porEmpresa = new Map();
  const emVoo = new Map();
  const geracoes = new Map();
  const ouvintes = new Set();

  const geracaoDe = companyId => geracoes.get(companyId) || 0;

  const avisar = (companyId, recarregar) => {
    ouvintes.forEach(fn => fn(companyId, recarregar));
  };

  const buscar = companyId => {
    const registro = emVoo.get(companyId);

    if (registro) return registro.promessa;

    const minhaGeracao = geracaoDe(companyId);

    const guardar = porConexao => {
      // Geração vencida: esta resposta conta o expediente de antes da
      // invalidação (ou de antes do logout). Ela não volta para o guardado.
      if (geracaoDe(companyId) !== minhaGeracao) return porConexao;

      porEmpresa.set(companyId, {
        porConexao,
        expiraEm: Date.now() + validadeMs
      });
      avisar(companyId, false);

      return porConexao;
    };

    const promessa = buscarDoServidor()
      .then(guardar)
      // Sem horário não há selo: ele é uma explicação, não um dado do
      // atendimento. Falhar em silêncio é melhor do que um toast por tela.
      .catch(() => guardar({}))
      .finally(() => {
        // Só apaga o registro se ele ainda for ESTE: uma invalidação no meio
        // do voo já pôs outra busca no lugar, e apagá-la faria a próxima
        // chamada disparar um terceiro pedido.
        if (emVoo.get(companyId)?.promessa === promessa) {
          emVoo.delete(companyId);
        }
      });

    emVoo.set(companyId, { promessa, geracao: minhaGeracao });

    return promessa;
  };

  // Toda empresa que tem qualquer coisa em memória — inclusive a que só tem
  // busca EM VOO. Sem as chaves de `emVoo`, o primeiro pedido de uma empresa
  // recém-carregada não tinha geração para vencer: ele voltava depois do
  // logout e repovoava o guardado com dado de quem já saiu.
  const empresasConhecidas = () =>
    new Set([...geracoes.keys(), ...porEmpresa.keys(), ...emVoo.keys()]);

  const esquecer = (companyId, recarregar) => {
    geracoes.set(companyId, geracaoDe(companyId) + 1);
    porEmpresa.delete(companyId);
    emVoo.delete(companyId);
    avisar(companyId, recarregar);
  };

  const esquecerTudo = recarregar => {
    empresasConhecidas().forEach(id =>
      geracoes.set(id, geracaoDe(id) + 1)
    );
    porEmpresa.clear();
    emVoo.clear();
    avisar(null, recarregar);
  };

  return {
    // Logout: esquece e NÃO pede recarga. Pedir era o que fazia um GET sair
    // logo depois de sair da conta e trazer o expediente de volta.
    limpar: companyId =>
      companyId ? esquecer(companyId, false) : esquecerTudo(false),

    // Salvou horário: esquece E pede que quem está na tela busque agora.
    invalidar: companyId =>
      companyId ? esquecer(companyId, true) : esquecerTudo(true),

    buscar,

    valorDe: companyId => porEmpresa.get(companyId)?.porConexao,

    venceu: companyId => {
      const atual = porEmpresa.get(companyId);

      return !atual || atual.expiraEm <= Date.now();
    },

    assinar: ouvinte => {
      ouvintes.add(ouvinte);

      return () => ouvintes.delete(ouvinte);
    }
  };
};

export default criarCacheDeExpediente;
